import React from "react"
import { formatRange } from "./formatters"

const geographyLabel = geography => {
  if (!geography || geography.scope === "global") return "Global · all activity locations"
  if (geography.scope === "country") return `Country · ${geography.country}`
  if (geography.scope === "region") return `${geography.country} › ${geography.region}`
  if (geography.scope === "city") return `${geography.country} › ${geography.region} › ${geography.city}`
  return `${geography.country} › Unmapped`
}

const sportLabel = (sport = "all") => (
  sport === "all"
    ? "All sports"
    : `${sport.charAt(0).toUpperCase()}${sport.slice(1)}`
)

export const QuerySummary = ({ snapshot }) => (
  <div className="analytics-query-summary" aria-live="polite">
    <div>
      <span>Current period</span>
      <strong>{formatRange(snapshot.ranges.current)}</strong>
      <small>{snapshot.ranges.current.days} inclusive days</small>
    </div>
    <div>
      <span>Comparison period</span>
      <strong>{formatRange(snapshot.ranges.comparison)}</strong>
      <small>
        {snapshot.ranges.comparison.label} · {snapshot.ranges.comparison.days} inclusive days
      </small>
    </div>
    <div>
      <span>Applied to every perspective</span>
      <strong>{sportLabel(snapshot.filters.sport)}</strong>
      <small>{geographyLabel(snapshot.filters.geography)}</small>
    </div>
  </div>
)

export const ComparisonChart = ({ chart, kpi }) => {
  const maximum = Math.max(kpi.current.value, kpi.comparison.value, 1)
  return (
    <section className="analytics-chart-card" aria-labelledby="analytics-comparison-title">
      <div className="analytics-section-heading">
        <div>
          <p className="analytics-eyebrow">Period comparison</p>
          <h2 id="analytics-comparison-title">Confirmed Participation</h2>
        </div>
        <span className="analytics-chart-legend"><i /> Current <i /> Comparison</span>
      </div>
      <div className="analytics-comparison-plot" role="img" aria-label={chart.accessibleSummary}>
        <div className="analytics-bar-row">
          <span>Current</span>
          <div><b style={{ width: `${(kpi.current.value / maximum) * 100}%` }} /></div>
          <strong>{kpi.current.value.toLocaleString("en-GB")}</strong>
        </div>
        <div className="analytics-bar-row is-comparison">
          <span>Comparison</span>
          <div><b style={{ width: `${(kpi.comparison.value / maximum) * 100}%` }} /></div>
          <strong>{kpi.comparison.value.toLocaleString("en-GB")}</strong>
        </div>
      </div>
      <p className="analytics-chart-summary">{chart.accessibleSummary}</p>
    </section>
  )
}

export const formatQualityCoverage = (quality = {}) => {
  const coverage = quality?.coverage || {}
  const parts = []
  if (Number.isFinite(coverage.percentage)) parts.push(`${coverage.percentage}% covered`)
  if (Number.isFinite(coverage.covered) && Number.isFinite(coverage.total)) {
    parts.push(`${coverage.covered} of ${coverage.total}`)
  }
  const through = coverage.dataAvailableThrough || coverage.through
  if (through) parts.push(`through ${through}`)
  if (coverage.requestedThrough) parts.push(`requested through ${coverage.requestedThrough}`)
  if (coverage.basis) parts.push(`basis: ${coverage.basis}`)
  if (Number.isFinite(coverage.reconciliationDiscrepancy)) {
    parts.push(`reconciliation discrepancy: ${coverage.reconciliationDiscrepancy}`)
  }
  return parts.length ? parts.join("; ") : "coverage details unavailable"
}

export const QualityBadge = ({ quality }) => {
  const state = typeof quality === "string" ? quality : quality?.state || "unavailable"
  const reason = typeof quality === "object" ? quality.reason : undefined
  const label = state.charAt(0).toUpperCase() + state.slice(1)
  const coverage = typeof quality === "object" ? formatQualityCoverage(quality) : "coverage details unavailable"
  const accessibleLabel = `${label}. ${reason || "Reason unavailable."} Coverage: ${coverage}.`
  return <em className={`is-${state}`} title={accessibleLabel} aria-label={accessibleLabel}>{label}</em>
}
